import { Grid, Box } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isAndroid, osVersion } from 'react-device-detect';
import PropTypes from 'prop-types';
import { PersonalBundleStyles as styleClasses } from './PersonalBundleStyles';

import LeadGenerationContext from '../../context/LeadGenerationContext';
import { CustomSelect } from '../LeadGeneration/CustomSelect/CustomSelect';
import { CustomDob } from '../LeadGeneration/CustomDob/CustomDob';
import { PersonalInformationStyles as classes } from '../LeadGeneration/StepOne/PersonalInformationStyles';
import { SSNAndroid } from '../LeadGeneration/SSN/SSNAndroid';
import { SSN } from '../LeadGeneration/SSN/SSN';
import {
  commonUtility,
  commonUtilityBooleanChk,
  commonUtilityValueReturn,
} from '../LeadGeneration/UtilityFunctions';
import {
  GENDER,
  INVALID_FORMAT_ERR,
  MANDATORY_FIELD_ERR,
  REQUIRED,
  DATE_FORMAT_EN,
  DATE_FORMAT_ES,
  EN,
  DATE_FORMATBUNDLE_ES,
} from '../../constants';
import {
  commonTernary,
  formatMaskedSSN,
  formatSSN,
  handleDOBchange,
  isBundle,
} from '../commonCode';

/* PersonalBundle is a child component to PersonalInformation */
function PersonalBundle(props) {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);

  const { handleChange } = props;

  const {
    personalInformation,
    setPersonalInformation,
    personalInformationErrors,
    setPersonalInformationErrors,
    setIsScreenTwoDirty,
    setIsScreenThreeDirty,
    setIsScreenFourDirty,
    setIsScreenFiveDirty,
    setIsFundingScreenDirty,
    language,
    idUploadStatus,
    vouchedData,
    saveAppContactInfo,
    setSaveAppContactInfo,
    setSaveAppContactInfoErrors,
    setIsFeatureScreenDirty,
    contactInformation,
    productName,
    saveAppContactInfoErrors,
  } = leadGenerationContext;

  useEffect(() => {
    if (
      contactInformation.firstName !== personalInformation.firstName ||
      contactInformation.lastName !== personalInformation.lastName
    ) {
      setPersonalInformation((prevState) => ({
        ...prevState,
        firstName: contactInformation.firstName,
        lastName: contactInformation.lastName,
      }));
    }
  }, [contactInformation.firstName, contactInformation.lastName]);

  /** SSN Handle change
   */
  const handleKeyDownSSN = (value) => {
    setIsScreenTwoDirty(true);
    setIsScreenThreeDirty(true);
    setIsScreenFourDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    setIsFeatureScreenDirty(true);
    if (value.match(/^\d/)) {
      if (value.length <= 11) {
        setPersonalInformation((prevState) => ({ ...prevState, ssn: value }));
        setPersonalInformationErrors((prevState) => ({
          ...prevState,
          ssn: '',
        }));
        setSaveAppContactInfo((prevState) => ({ ...prevState, ssn: value }));
        setSaveAppContactInfoErrors((prevState) => ({
          ...prevState,
          ssn: '',
        }));
        if (value.length < 11) {
          setPersonalInformationErrors((prevState) => ({
            ...prevState,
            ssn: '* Social Security Number should have 9 digits',
          }));
          setSaveAppContactInfoErrors((prevState) => ({
            ...prevState,
            ssn: '* Social Security Number should have 9 digits',
          }));
        } else {
          setPersonalInformationErrors((prevState) => ({
            ...prevState,
            ssn: '',
          }));
          setSaveAppContactInfoErrors((prevState) => ({
            ...prevState,
            ssn: '',
          }));
        }
      }
    } else if ((value ?? '') === '') {
      setPersonalInformation((prevState) => ({ ...prevState, ssn: '' }));
      setPersonalInformationErrors((prevState) => ({
        ...prevState,
        ssn: MANDATORY_FIELD_ERR,
      }));
      setSaveAppContactInfo((prevState) => ({ ...prevState, ssn: '' }));
      setSaveAppContactInfoErrors((prevState) => ({
        ...prevState,
        ssn: MANDATORY_FIELD_ERR,
      }));
    } else {
      setPersonalInformationErrors((prevState) => ({
        ...prevState,
        ssn: INVALID_FORMAT_ERR,
      }));
      setSaveAppContactInfoErrors((prevState) => ({
        ...prevState,
        ssn: INVALID_FORMAT_ERR,
      }));
    }
  };
  /** SSN Handle change for android
   */
  const handleKeyDownSSNAndroid = (e) => {
    const isMasked = e.target.value.includes('X');

    let value = isMasked
      ? formatMaskedSSN(
          e,
          personalInformation,
          INVALID_FORMAT_ERR,
          setPersonalInformationErrors,
          setSaveAppContactInfoErrors
        )
      : e.target.value;

    setIsScreenTwoDirty(true);
    setIsScreenThreeDirty(true);
    setIsScreenFourDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    setIsFeatureScreenDirty(true);
    if (value.match(/^[0-9-]*$/)) {
      value = isMasked ? value : formatSSN(e, personalInformation);
      if (value.length <= 11) {
        leadGenerationContext.setPersonalInformation((prevState) => ({ ...prevState, ssn: value }));
        leadGenerationContext.setPersonalInformationErrors((prevState) => ({
          ...prevState,
          ssn: '',
        }));
        setSaveAppContactInfo((prevState) => ({ ...prevState, ssn: value }));
        setSaveAppContactInfoErrors((prevState) => ({
          ...prevState,
          ssn: '',
        }));
        if (value.length < 11 && value.length !== 0) {
          leadGenerationContext.setPersonalInformationErrors((prevState) => ({
            ...prevState,
            ssn: '* Social Security Number should have 9 digits',
          }));
          setSaveAppContactInfoErrors((prevState) => ({
            ...prevState,
            ssn: '* Social Security Number should have 9 digits',
          }));
        } else if (value.length === 0) {
          leadGenerationContext.setPersonalInformationErrors((prevState) => ({
            ...prevState,
            ssn: MANDATORY_FIELD_ERR,
          }));
          setSaveAppContactInfoErrors((prevState) => ({
            ...prevState,
            ssn: MANDATORY_FIELD_ERR,
          }));
        } else {
          leadGenerationContext.setPersonalInformationErrors((prevState) => ({
            ...prevState,
            ssn: '',
          }));
          setSaveAppContactInfoErrors((prevState) => ({
            ...prevState,
            ssn: '',
          }));
        }
      }
    } else if ((value ?? '') === '') {
      leadGenerationContext.setPersonalInformation((prevState) => ({ ...prevState, ssn: '' }));
      leadGenerationContext.setPersonalInformationErrors((prevState) => ({
        ...prevState,
        ssn: MANDATORY_FIELD_ERR,
      }));
      setSaveAppContactInfo((prevState) => ({ ...prevState, ssn: '' }));
      setSaveAppContactInfoErrors((prevState) => ({
        ...prevState,
        ssn: MANDATORY_FIELD_ERR,
      }));
    } else {
      leadGenerationContext.setPersonalInformationErrors((prevState) => ({
        ...prevState,
        ssn: INVALID_FORMAT_ERR,
      }));
      setSaveAppContactInfoErrors((prevState) => ({
        ...prevState,
        ssn: INVALID_FORMAT_ERR,
      }));
    }
  };

  return (
    /* PersonalBundle component JSX content */
    <Grid container spacing={0} data-page="personal-info">
      <Grid container sx={styleClasses.container}>
        <Grid sx={styleClasses.field}>
          {isAndroid && osVersion?.split('.')[0] <= 10 ? (
            <SSNAndroid
              isBundle
              handleKeyDown={handleKeyDownSSNAndroid}
              disabled={commonUtilityBooleanChk(
                (saveAppContactInfo?.ssn !== '' && leadGenerationContext.isApplicationSaved) ||
                  leadGenerationContext.savedDataFlow
              )}
              error={commonUtility(personalInformationErrors.ssn)}
              ssn={personalInformation.ssn}
              id="ssn"
            />
          ) : (
            <SSN
              isBundle
              handleKeyDown={handleKeyDownSSN}
              disabled={commonUtilityBooleanChk(
                (saveAppContactInfo?.ssn !== '' && leadGenerationContext.isApplicationSaved) ||
                  leadGenerationContext.savedDataFlow
              )}
              error={commonUtility(personalInformationErrors.ssn)}
              ssn={personalInformation.ssn}
              id="ssn"
            />
          )}

          <Box component="span" sx={classes.error_message} role="alert">
            {t(personalInformationErrors?.ssn, {
              defaultValue: personalInformationErrors?.ssn,
            })}
          </Box>
        </Grid>
        <Grid sx={styleClasses.field}>
          <CustomSelect
            isBundle
            id="gender"
            dataTestId="gender_id"
            inputLabelAriaLabel={`${t(GENDER, {
              defaultValue: GENDER,
            })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
            label={
              <span style={{ color: personalInformationErrors?.gender ? 'red' : 'inherit' }}>
                {t(GENDER, { defaultValue: GENDER })}
              </span>
            }
            options={['Female', 'Male']}
            handleChange={(e) => handleChange(e, leadGenerationContext)}
            fullWidth
            disabled={commonUtilityBooleanChk(
              (idUploadStatus &&
                (vouchedData?.gender?.gender === 'woman' ||
                  vouchedData?.gender?.gender === 'man')) ||
                (personalInformation?.gender !== '' &&
                  (leadGenerationContext.savedDataFlow ||
                    leadGenerationContext.isApplicationSaved) &&
                  !isBundle(productName))
            )}
            value={commonUtility(personalInformation?.gender)}
            error={personalInformationErrors?.gender}
            selectAriaLabel={`${t('Aria Label Gender', {
              defaultValue: 'Gender Required Select Gender dropdown menu button',
            })} ${t(REQUIRED, { defaultValue: REQUIRED, nsSeparator: '|' })}`}
          />
          <Box component="span" role="alert" sx={classes.error_message} data-testid="genderError">
            {t(personalInformationErrors?.gender, {
              defaultValue: personalInformationErrors?.gender,
            })}
          </Box>
        </Grid>

        <Grid sx={styleClasses.field}>
          <CustomDob
            isBundle
            label={t(`Date of birth`, {
              defaultValue: `Date of birth`,
            })}
            id="dob"
            maxDatePicker={new Date()}
            placeholder={commonTernary(
              language === EN,
              t(DATE_FORMAT_EN, { defaultValue: DATE_FORMAT_EN }),
              t(DATE_FORMATBUNDLE_ES, { defaultValue: DATE_FORMATBUNDLE_ES })
            )}
            inputFormat={commonTernary(
              language === EN,
              t('MM/DD/YYYY', { defaultValue: 'MM/DD/YYYY' }),
              t('DD/MM/YYYY', { defaultValue: 'DD/MM/YYYY' })
            )}
            dataTestIdForLabel="dobTestId"
            dataTestId="date-of-birth"
            value={personalInformation.dob}
            onChange={(newValue) => handleDOBchange(newValue, leadGenerationContext)}
            fullWidth
            language={language}
            errorBorder={personalInformationErrors?.dob}
            // disabled={
            //   commonUtilityBooleanChk(
            //     (personalInformation?.uploadSuccessStatus && vouchedData?.birthDate !== null) ||
            //       leadGenerationContext.savedDataFlow ||
            //       (saveAppContactInfo?.dob !== '' && leadGenerationContext.isApplicationSaved)
            //   ) &&
            //   (personalInformationErrors?.dob === null || personalInformationErrors?.dob === '')
            // }
            disabled={commonUtilityBooleanChk(
              (idUploadStatus && vouchedData?.birthDate !== null) ||
                personalInformation?.uploadSuccessStatus ||
                leadGenerationContext.savedDataFlow ||
                (saveAppContactInfo?.dob !== '' && leadGenerationContext.isApplicationSaved)
            )}
            dob={commonTernary(personalInformation?.dob, personalInformation.dob, '')}
          />
          <Box
            component="span"
            role="alert"
            tabIndex="-1"
            sx={classes.error_message}
            data-testid="dobError"
          >
            {t(personalInformationErrors?.dob, {
              defaultValue: personalInformationErrors?.dob,
            })}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default PersonalBundle;

PersonalBundle.propTypes = {
  handleChange: PropTypes.func,
};
